import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Activity } from 'lucide-react';
import TurkeyMap from './TurkeyMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const POLL_INTERVAL_MS = 15000;
const CYCLE_INTERVAL_MS = 3200;
const CYCLE_MAX_ROUNDS = 40;

const LiveActivityFeed = () => {
  const [visible, setVisible] = useState(false);
  const containerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const mapRef = useRef(null);
  const seenEventKeysRef = useRef(new Set());
  const initialLoadRef = useRef(true);
  const cycleQueueRef = useRef([]);
  const cycleIdxRef = useRef(0);
  const cycleTimerRef = useRef(null);

  const buildKey = (e) => `${e.type}|${e.maskedName}|${e.venueName}|${e.when}`;

  const startCycle = useCallback((events) => {
    // Keep only events that have a city we can pulse
    const eligible = events.filter((e) => e && e.city);
    if (eligible.length === 0) return;
    cycleQueueRef.current = eligible;
    cycleIdxRef.current = 0;

    const tick = () => {
      const q = cycleQueueRef.current;
      if (!q || q.length === 0 || !mapRef.current) return;
      const evt = q[cycleIdxRef.current % q.length];
      try { mapRef.current.pulseCity(evt.city, evt); } catch (e) { /* ignore */ }
      cycleIdxRef.current += 1;
      if (cycleIdxRef.current < CYCLE_MAX_ROUNDS) {
        cycleTimerRef.current = setTimeout(tick, CYCLE_INTERVAL_MS);
      }
    };
    // Delay the first pulse a bit so the map has time to render
    cycleTimerRef.current = setTimeout(tick, 1200);
  }, []);

  const fetchFeed = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/live-activity/feed`);
      if (!res.ok) return;
      const data = await res.json();
      if (!data.success) return;
      const nextEvents = data.events || [];

      if (initialLoadRef.current) {
        // Seed known events + kick off cycle-pulse showcase
        nextEvents.forEach((e) => seenEventKeysRef.current.add(buildKey(e)));
        initialLoadRef.current = false;
        startCycle(nextEvents);
      } else {
        // Detect real new events and pulse them immediately with a tooltip
        nextEvents.forEach((e) => {
          const k = buildKey(e);
          if (!seenEventKeysRef.current.has(k)) {
            seenEventKeysRef.current.add(k);
            if (e.city && mapRef.current) {
              try { mapRef.current.pulseCity(e.city, e); } catch (err) { /* ignore */ }
            }
          }
        });
        // Also refresh the cycle queue so cycle pulses reflect latest events
        cycleQueueRef.current = nextEvents.filter((e) => e && e.city);
      }
    } catch (e) { /* silent */ }
  }, [startCycle]);

  useEffect(() => {
    if (!containerRef.current) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((entry) => setVisible(entry.isIntersecting)),
      { rootMargin: '200px 0px', threshold: 0.05 }
    );
    io.observe(containerRef.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) {
      if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
      return;
    }
    fetchFeed();
    const tick = () => { if (document.visibilityState === 'visible') fetchFeed(); };
    pollTimerRef.current = setInterval(tick, POLL_INTERVAL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') fetchFeed(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [visible, fetchFeed]);

  // Cleanup cycle timer on unmount
  useEffect(() => () => {
    if (cycleTimerRef.current) clearTimeout(cycleTimerRef.current);
  }, []);

  return (
    <section
      ref={containerRef}
      className="py-12 bg-gradient-to-b from-white via-rose-50/40 to-white"
      data-testid="live-activity-feed"
    >
      <style>{`
        @keyframes livePulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
      `}</style>

      <div className="container mx-auto px-4 max-w-6xl">
        <div className="relative rounded-3xl bg-white ring-1 ring-gray-200 shadow-[0_6px_40px_-16px_rgba(244,63,94,0.28)] overflow-hidden">
          {/* Top gradient accent */}
          <div className="h-1 bg-gradient-to-r from-rose-400 via-pink-500 to-rose-600" />

          {/* Minimal header strip */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gradient-to-r from-white to-rose-50/40">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-rose-500" strokeWidth={2.4} />
              <span className="text-[13px] font-semibold text-gray-700">Canlı Aktivite Haritası</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span
                className="h-1.5 w-1.5 rounded-full bg-emerald-500"
                style={{ animation: 'livePulse 1.4s ease-in-out infinite' }}
              />
              <span className="text-[11px] font-medium text-emerald-600 uppercase tracking-wider">Yayında</span>
            </div>
          </div>

          {/* Map — the entire visual */}
          <div className="px-4 sm:px-6 py-5">
            <TurkeyMap ref={mapRef} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveActivityFeed;
